import { PostAccountStepId } from '../ActivationFlow/constants/activationStepIds';

/**
 * Card type from the cards API
 */
interface Card {
  status?: string;
  [key: string]: any;
}

/**
 * Result of auto-skip check
 */
export interface AutoSkipResult {
  shouldSkip: boolean;
  targetStepId?: string;
  skipReason?: string;
}

/**
 * Centralized logic for determining if issue_card step should be auto-skipped
 *
 * BUSINESS RULE: If user already has an enabled card, skip directly to "Your Card Ready"
 * This prevents asking user to create a card they already have
 *
 * @param cards - List of user's cards from API
 * @param currentStepId - ID of current step being evaluated
 * @returns Auto-skip decision with target step if applicable
 */
export const shouldAutoSkipIssueCard = (
  cards: Card[] | undefined,
  currentStepId: string,
): AutoSkipResult => {
  // Only applies to issue_card step
  if (currentStepId !== PostAccountStepId.IssueCard) {
    return { shouldSkip: false };
  }

  // Check if user has at least one enabled card
  const hasEnabledCard = cards?.some((card) => card?.status === 'Enabled');

  if (hasEnabledCard) {
    return {
      shouldSkip: true,
      targetStepId: PostAccountStepId.YourCardReady,
      skipReason: 'User already has an enabled card',
    };
  }

  return { shouldSkip: false };
};

/**
 * Centralized logic for determining if card_design step should be auto-skipped
 *
 * BUSINESS RULE: When returning from Swan after card creation (via ?resumeActivation=card_design),
 * if user now has an enabled card, skip directly to "Your Card Ready"
 * This handles the post-Swan redirect scenario where card was just created
 *
 * @param cards - List of user's cards from API
 * @param currentStepId - ID of current step being evaluated
 * @returns Auto-skip decision with target step if applicable
 */
export const shouldAutoSkipCardDesign = (
  cards: Card[] | undefined,
  currentStepId: string,
): AutoSkipResult => {
  // Only applies to card_design step
  if (currentStepId !== PostAccountStepId.CardDesign) {
    return { shouldSkip: false };
  }

  // Check if user has at least one enabled card (just created via Swan)
  const hasEnabledCard = cards?.some((card) => card?.status === 'Enabled');

  if (hasEnabledCard) {
    return {
      shouldSkip: true,
      targetStepId: PostAccountStepId.YourCardReady,
      skipReason: 'User has enabled card after Swan redirect',
    };
  }

  return { shouldSkip: false };
};

/**
 * Apply auto-skip logic and update auto-skipped steps tracking
 *
 * @param substepId - The substep ID we might skip to
 * @param autoSkipResult - Result from shouldAutoSkipIssueCard
 * @param visibleSubsteps - Array of all visible substeps
 * @returns Index to navigate to, or -1 if not found
 */
export const applyAutoSkip = (
  substepId: string,
  autoSkipResult: AutoSkipResult,
  visibleSubsteps: { id: string }[],
): number => {
  if (!autoSkipResult.shouldSkip || !autoSkipResult.targetStepId) {
    return -1;
  }

  const targetIndex = visibleSubsteps.findIndex(
    (s) => s.id === autoSkipResult.targetStepId,
  );

  if (targetIndex !== -1 && process.env.NODE_ENV === 'development') {
    console.log(
      `[Auto-Skip] Skipping ${substepId} → ${autoSkipResult.targetStepId}`,
      `Reason: ${autoSkipResult.skipReason}`,
    );
  }

  return targetIndex;
};
